import { useCallback } from 'react'
import {
  upsertOverlay,
  deleteOverlay as deleteSeasonOverlay,
  presignSvgUpload,
  uploadSvgToS3,
} from '../../../Apis/overlays'
import { upsertDefaultOverlay } from '../../../Apis/defaultOverlays'
import { fromBox } from './placementModel'

// Extracts the SVG text behind a `data:image/svg+xml;base64,...` URI. The default-overlay
// editor receives raw SVG markup from the API and the parent wraps it into a data URI so
// the editor's `<img src>` and SVG-parse logic keep working unchanged. To round-trip the
// markup back to the PUT endpoint we have to undo that wrapping. For object/blob URLs we
// fall back to `fetch(...).text()`.
async function readSvgText(url) {
  if (!url) return ''
  const dataUriPrefix = 'data:image/svg+xml;base64,'
  if (url.startsWith(dataUriPrefix)) {
    try {
      // Decode base64 to bytes, then UTF-8-decode so non-ASCII content (Greek
      // text, emoji, accented characters) round-trips correctly. A bare
      // `atob(...)` returns a binary string and would mangle multi-byte UTF-8.
      const base64 = url.slice(dataUriPrefix.length)
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
      return new TextDecoder().decode(bytes)
    } catch {
      // fall through to fetch
    }
  }
  const resp = await fetch(url)
  return resp.text()
}

/**
 * Per-mode save/delete adapter for the overlay editor.
 *
 * The editor's UX (drag, drop, configure placements) is identical for season-scoped and
 * default-overlay editing — only the persistence layer differs:
 *   - season mode  : presign + upload SVG to S3, then PUT placements + S3 URL to the
 *                    season endpoint.
 *   - default mode : PUT { svg, placements } directly to the dev-only default-overlays
 *                    endpoint (no S3 step; SVG is written to disk).
 *
 * The hook abstracts both flows behind a uniform `save(state)` call so the editor stays
 * agnostic. Load is still handled at the parent level (BrandingTab vs the Task-11 route
 * page) because each parent has its own list+selection logic.
 */
export function useOverlayDataSource({ mode = 'season', orgId, seasonId, type, variant, sport, statsMode }) {
  const save = useCallback(async ({
    pendingFile,
    savedSvgUrl,
    placements,
    templateScale,
    templatePosition,
    templateCrop,
    x,
    y,
    homeBarColor,
    awayBarColor,
    barColor,
    targetMode,
    supportsTargetMode,
  }) => {
    // Persist IMAGE placements in the backend's center-anchored convention
    // (editor holds them top-left) so saved overlays round-trip and render
    // identically to the preview. Text/pills pass through unchanged.
    const storedPlacements = placements.map(fromBox)
    if (mode === 'default') {
      // Default mode: PUT raw SVG + placements to the dev-only endpoint. If the user
      // uploaded a new file in this session, read its text; otherwise round-trip the
      // currently-displayed data URI back to text.
      let svgText
      if (pendingFile) {
        svgText = await pendingFile.text()
      } else {
        svgText = await readSvgText(savedSvgUrl)
      }
      await upsertDefaultOverlay(
        type,
        { svg: svgText, placements: storedPlacements },
        variant,
        sport,
        statsMode,
      )
      // Default mode never mutates the displayed URL (the parent owns reload).
      return { savedSvgUrl }
    }

    // Season mode: existing presign + S3 + PUT flow. Team Shots brands home/away as
    // separate rows, so every call must carry the variant (the backend rejects a Team
    // Shots write that omits it, and defaults everything else to 0).
    let svgUrl = savedSvgUrl
    if (pendingFile) {
      const signedUrl = await presignSvgUpload(orgId, seasonId, type, pendingFile.name, variant ?? 0)
      await uploadSvgToS3(signedUrl, pendingFile)
      svgUrl = signedUrl.split('?')[0]
    }
    await upsertOverlay(orgId, seasonId, type, {
      templateSvgUrl: svgUrl,
      placements: storedPlacements,
      templateScale,
      templatePosition,
      templateCrop,
      x,
      y,
      homeBarColor,
      awayBarColor,
      barColor,
      targetMode: supportsTargetMode ? targetMode : undefined,
    }, variant ?? 0)
    return { savedSvgUrl: svgUrl }
  }, [mode, orgId, seasonId, type, variant, sport, statsMode])

  const remove = useCallback(async () => {
    if (mode === 'default') {
      // No delete semantics for bundled defaults — the file always exists on disk. The
      // editor hides the "Reset to Default" button in this mode, so this branch is just
      // defensive.
      return
    }
    await deleteSeasonOverlay(orgId, seasonId, type, variant ?? 0)
  }, [mode, orgId, seasonId, type, variant])

  return { save, remove }
}
